import { omit } from 'lodash-es';
import { NextRouter, useRouter } from 'next/router';
import { useEffect, useMemo, useRef } from 'react';

import { JOB_BOARD, JOBS } from '@pflegia/ui/pflegia/constants/routes';
import { stripFlavor } from '@pflegia/util/common/path-utils';
import { removeTrailingSlash } from '@pflegia/util/common/remove-trailing-slash';

import { parseJobsPageFromSlugs } from '../utils/parse-jobs-page-from-slugs';

const ROUTER_URL_BASE = process.env.NEXT_PUBLIC_BASE_URL ?? 'http://localhost:4300';
const JOBS_ROUTE_PREFIXES = new Set([JOB_BOARD, JOBS]);

export const normalizeSyncedJobBoardPath = (path: string): string => {
  const url = new URL(path, ROUTER_URL_BASE);
  const search = url.searchParams.toString();
  return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
};

export const buildSyncedJobBoardRoutePath = ({
  canonicalPath,
  currentPath,
  shouldResetPage = false,
}: {
  canonicalPath: string;
  currentPath: string;
  shouldResetPage?: boolean;
}) => {
  const currentUrl = new URL(currentPath, ROUTER_URL_BASE);
  const canonicalUrl = new URL(canonicalPath, ROUTER_URL_BASE);
  const currentRoutePrefix = removeTrailingSlash(`/${currentUrl.pathname.split('/').find(Boolean) ?? ''}`);

  if (!JOBS_ROUTE_PREFIXES.has(currentRoutePrefix)) return null;

  currentUrl.searchParams.forEach((value, key) => canonicalUrl.searchParams.set(key, value));

  const currentSlugs = currentUrl.pathname.split('/').filter(Boolean).slice(1);
  const { page: currentPage } = parseJobsPageFromSlugs(currentSlugs);
  const currentListingPathname = removeTrailingSlash(currentUrl.pathname.replace(/\/\d+\/?$/, '/'));
  const isListingPathChanged = currentListingPathname !== removeTrailingSlash(canonicalUrl.pathname);
  const shouldKeepPage = currentPage > 1 && !(shouldResetPage && isListingPathChanged);

  if (shouldKeepPage) {
    canonicalUrl.pathname = `${removeTrailingSlash(canonicalUrl.pathname)}/${currentPage}`;
  }
  canonicalUrl.hash = currentUrl.hash;

  return normalizeSyncedJobBoardPath(`${canonicalUrl.pathname}${canonicalUrl.search}${canonicalUrl.hash}`);
};

export const replaceSyncedJobBoardRoute = ({
  router,
  syncedRoutePath,
}: {
  router: Pick<NextRouter, 'query' | 'replace'>;
  syncedRoutePath: string;
}) => {
  const nextSlugs = new URL(syncedRoutePath, ROUTER_URL_BASE).pathname.split('/').filter(Boolean).slice(1);
  const nextQuery = omit(router.query, ['slugs']);

  return router.replace(
    {
      query: nextSlugs.length > 0 ? { ...nextQuery, slugs: nextSlugs } : nextQuery,
    },
    syncedRoutePath,
    { shallow: true, scroll: false },
  );
};

export const useSyncJobBoardRoute = ({
  canonicalPath,
  isEnabled = true,
  requestedSyncedRoutePath,
  shouldResetPageOnSync = false,
}: {
  canonicalPath: string;
  isEnabled?: boolean;
  requestedSyncedRoutePath?: string | null;
  shouldResetPageOnSync?: boolean;
}) => {
  const router = useRouter();
  const strippedAsPath = useMemo(() => stripFlavor(router.asPath), [router.asPath]);
  const lastSyncedPathRef = useRef<string | null>(null);
  const syncedRoutePath = useMemo(
    () => buildSyncedJobBoardRoutePath({ canonicalPath, currentPath: strippedAsPath, shouldResetPage: shouldResetPageOnSync }),
    [canonicalPath, strippedAsPath, shouldResetPageOnSync],
  );
  const isRouteSyncPending = Boolean(isEnabled && syncedRoutePath && normalizeSyncedJobBoardPath(strippedAsPath) !== syncedRoutePath);

  useEffect(() => {
    if (!isEnabled || !router.isReady || !syncedRoutePath) return;

    if (normalizeSyncedJobBoardPath(strippedAsPath) === syncedRoutePath) {
      lastSyncedPathRef.current = syncedRoutePath;
      return;
    }
    if (requestedSyncedRoutePath === syncedRoutePath) {
      lastSyncedPathRef.current = syncedRoutePath;
      return;
    }
    if (lastSyncedPathRef.current === syncedRoutePath) return;

    lastSyncedPathRef.current = syncedRoutePath;

    replaceSyncedJobBoardRoute({ router, syncedRoutePath });
  }, [isEnabled, requestedSyncedRoutePath, router, router.isReady, strippedAsPath, syncedRoutePath]);

  return { isRouteSyncPending };
};
